import React, { useState } from 'react';
import { Shield, Check, HelpCircle, ChevronDown, ChevronUp, Target, Clock, Award, FileCheck, Users, Zap, Building } from 'lucide-react';
import UniversalPopupForm from '../../components/UniversalPopupForm';
import SEO from '../../components/SEO';

const CompanyFormation = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      title: 'Limited Liability',
      description: 'Protect your personal assets from business debts and legal liabilities with corporate structure.'
    },
    {
      title: 'Tax Advantages',
      description: 'Access Canadian small business tax rates and income splitting opportunities for owners.'
    },
    {
      title: 'Enhanced Credibility',
      description: 'Build trust with customers, suppliers, and investors through professional incorporation.'
    },
    {
      title: 'Access to Capital',
      description: 'Easier financing and investment opportunities with a properly structured corporation.'
    },
    {
      title: 'Perpetual Existence',
      description: 'Your business continues beyond individual ownership, enabling long-term planning and succession.'
    },
    {
      title: 'National Protection',
      description: 'Federal incorporation provides nationwide name protection across all Canadian provinces.'
    }
  ];

  const faqs = [
    {
      question: 'What is the difference between federal and provincial incorporation?',
      answer: 'Federal incorporation provides nationwide name protection and allows operations across Canada. Provincial incorporation is limited to one province but typically has lower costs. We help you choose the right option for your business needs.'
    },
    {
      question: 'How long does company formation take in Canada?',
      answer: 'Federal incorporation typically takes 5-10 business days once all documents are submitted. Provincial incorporation can be faster, often 3-5 business days. We handle all paperwork to expedite the process.'
    },
    {
      question: 'What documents do I need to incorporate?',
      answer: 'You\'ll need to provide company name choices, business activities description, shareholder and director information, and registered office address. Our team guides you through gathering all necessary documentation.'
    },
    {
      question: 'Can I incorporate with just one person?',
      answer: 'Yes, most Canadian jurisdictions allow single-person corporations. You can be the sole shareholder, director, and officer. We assist with proper structure for single-owner businesses.'
    },
    {
      question: 'What ongoing obligations does a corporation have?',
      answer: 'Corporations must file annual returns, maintain corporate records, hold director meetings, and file corporate tax returns. We provide guidance on meeting all compliance requirements.'
    },
    {
      question: 'How much does it cost to incorporate in Canada?',
      answer: 'Costs vary based on federal vs. provincial incorporation and additional services needed. Government fees range from $200-$500, plus our professional service fees. We provide transparent pricing upfront.'
    }
  ];

  const formationPricing = [
    {
      name: 'Basic',
      features: [
        'Name Search & Reservation',
        'Articles of Incorporation',
        'Federal or Provincial Filing',
        'Corporate Number',
        'Digital Certificate Delivery'
      ],
      recommended: false,
      badge: null
    },
    {
      name: 'Standard',
      features: [
        'Everything in Basic',
        'Corporate Bylaws',
        'Share Certificates',
        'Minute Book Setup',
        'Director Resolutions',
        'Banking Documents Package',
        'Email & Phone Support'
      ],
      recommended: true,
      badge: 'Recommended'
    },
    {
      name: 'Deluxe',
      features: [
        'Everything in Standard',
        'Business Number (CRA)',
        'GST/HST Registration',
        'Payroll Account Setup',
        'Legal Consultation (1 hour)',
        'Shareholder Agreement Template',
        'First Year Compliance Guide',
        'Priority Processing',
        'Lifetime Support'
      ],
      recommended: false,
      badge: 'Best Seller'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Company Formation Services"
        description="Professional Canadian company incorporation services. Form your business with expert guidance, limited liability protection, and tax advantages."
        keywords="company formation Canada, business incorporation, incorporate in Canada, Canadian business registration, corporate structure setup"
      />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c"
            alt="Business incorporation and company formation"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-teal-600/30 backdrop-blur-sm border border-teal-400/30 rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold">Canadian Company Formation</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">
              Company Formation Services
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed mb-8">
              Incorporate your business in Canada with expert guidance. Fast, reliable, and affordable incorporation services.
            </p>
            <button
              onClick={() => setIsPopupOpen(true)}
              className="bg-yellow-400 text-slate-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-500 hover:scale-110 transform hover:scale-105 transition-all duration-200 shadow-xl"
            >
              Start Your Incorporation
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Professional Help */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-teal-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1521587760476-6c12a4b040da"
                alt="Professional business incorporation consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-green-100 rounded-full p-2">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="font-bold text-gray-900">Expert Formation</span>
                </div>
                <p className="text-sm text-gray-600">Professional incorporation filing</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-teal-100 text-teal-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Advantage
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Professional Help Matters
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Incorporating a business involves complex legal requirements and strategic decisions. Our experts ensure your company is structured correctly from day one, avoiding costly mistakes and future complications.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                    <Target className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Optimal Structure</h3>
                    <p className="text-gray-600">Choose the right corporate structure for your business goals and tax situation.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Complete Compliance</h3>
                    <p className="text-gray-600">All federal and provincial requirements met with precision and accuracy.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-100 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Save Time & Stress</h3>
                    <p className="text-gray-600">Focus on your business while we handle all incorporation paperwork and filings.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-b from-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Incorporation
            </h2>
            <p className="text-lg text-gray-600">
              Why Canadian businesses choose incorporation
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-blue-100 min-w-[85%] md:min-w-0 snap-center"
              >
                <div className="bg-teal-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Incorporation Process Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-green-100 text-green-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Formation Expertise
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigate Canadian Incorporation With Confidence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Whether you're choosing federal or provincial incorporation, our team has successfully formed hundreds of Canadian companies and knows exactly what's required for a smooth process.
              </p>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl">What We Handle:</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Company name search and clearance</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Articles of incorporation preparation</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Corporate bylaws and structure</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Federal and provincial filings</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-teal-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Corporate records and minute book</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-teal-600 text-white font-semibold px-8 py-4 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Start Your Application
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-green-400 to-blue-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c"
                alt="Incorporation filing process and legal documents"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Incorporation</div>
                    <div className="text-sm text-gray-600">Experts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fast & Reliable Service */}
      <section className="py-20 bg-gradient-to-b from-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
                alt="Fast company formation processing"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-600 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Zap className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">5-10 Day Processing</div>
                <div className="text-sm text-slate-200">Fast incorporation service</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Quick & Efficient
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fast Processing, Quality Results
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We understand that getting your business incorporated quickly is important. Our streamlined process ensures your company formation is completed efficiently without sacrificing accuracy or compliance.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-full p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quick Turnaround</h3>
                    <p className="text-gray-600">Federal incorporation completed within 5-10 business days, provincial even faster.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-full p-3 flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Dedicated Support</h3>
                    <p className="text-gray-600">Personal case manager guides you through every step of the incorporation process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-teal-600 rounded-full p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Quality Guaranteed</h3>
                    <p className="text-gray-600">Expert handling ensures your incorporation is complete, accurate, and compliant.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our 3-Step Formation Process
            </h2>
            <p className="text-lg text-gray-600">
              Simple, transparent, and efficient company incorporation
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-[85%] md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                1
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Consultation & Planning
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We review your business plans and help you choose the optimal corporate structure, whether federal or provincial incorporation best suits your needs.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-[85%] md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                2
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Filing & Registration
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our experts prepare all documents and file your incorporation with Corporations Canada or provincial authorities, ensuring full compliance with regulations.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg border border-blue-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-[85%] md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                3
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Corporate Records
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Receive your certificate of incorporation, corporate bylaws, and complete minute book, ready to operate your Canadian business immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Company Formation Packages
            </h2>
            <p className="text-lg text-gray-600">
              Choose the incorporation package that fits your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {formationPricing.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                  plan.recommended ? 'ring-2 ring-blue-600 scale-105' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-teal-600 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    {plan.badge}
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <p className="text-gray-600 text-sm">Contact us for pricing</p>
                  </div>
                  <ul className="space-y-3 mb-8 max-h-64 overflow-y-auto">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setIsPopupOpen(true)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.recommended
                        ? 'bg-teal-600 text-white hover:bg-yellow-400 hover:text-slate-900'
                        : 'bg-slate-200 text-slate-900 hover:bg-yellow-400 font-semibold'
                    }`}
                  >
                    Get a Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about Canadian company formation
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7"
                alt="Business incorporation consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our incorporation experts are here to help answer all your company formation questions.</p>
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="w-full bg-teal-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-yellow-400 hover:to-yellow-500 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-slate-900 via-teal-900 to-slate-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
            alt="Toronto business district and Canadian companies"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-900/90 to-slate-900/95"></div>
          
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by 500+ Canadian Businesses</span>
            </div>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Turn Your Vision Into<br />
            <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
              A Legal Canadian Business
            </span>
          </h2>
          
          <p className="text-2xl text-slate-200 mb-12 max-w-3xl mx-auto leading-relaxed">
            Start your company formation today with Canada's trusted incorporation experts. Federal and provincial options available.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group relative bg-white text-teal-600 font-bold px-10 py-5 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-lg">Incorporate Your Business</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300"></div>
            </button>
            <button
              className="js-chat group relative bg-teal-600 text-white font-bold px-10 py-5 rounded-xl hover:bg-yellow-400 hover:text-slate-900 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 text-lg">Chat Now</span>
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">5-10</div>
              <div className="text-slate-200 text-sm">Day Processing</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">500+</div>
              <div className="text-slate-200 text-sm">Companies Formed</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">Expert</div>
              <div className="text-slate-200 text-sm">Guidance</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">100%</div>
              <div className="text-slate-200 text-sm">Compliance</div>
            </div>
          </div>

          <p className="text-blue-200 mt-12 text-sm">
            Protecting Canadian businesses coast to coast • Professional Incorporation Services
          </p>
        </div>

        <div className="absolute top-10 left-10 w-20 h-20 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default CompanyFormation;
